/**
 * MEL (Minimum Equipment List) Types
 * Types for MEL lookup and tracking features
 *
 * These types match the backend Prisma schema and API response format.
 */

/**
 * MEL Document - represents an uploaded MEL document
 */
export interface MelDocument {
  id: number;
  aircraftType: string;
  version: string | null;
  originalFilename: string;
  filePath: string;
  parsedAt: string | null;
  itemCount: number;
  parseError: string | null;
  createdAt: string;
  updatedAt: string;
  items?: MelItem[];
}

/**
 * MEL Item - a single item from the MEL (matches Prisma MelItem model)
 */
export interface MelItem {
  id: number;
  melDocumentId: number;
  ataChapter: string;
  itemNumber: string;
  title: string;
  repairCategory: string;
  timeLimitDays: number | null;
  timeLimitFlightHours: number | null;
  numberInstalled: number;
  numberRequired: number;
  remarks: string | null;
  operationalProcedures: string | null;
  maintenanceProcedures: string | null;
  placardRequired: boolean;
  fullText: string | null;
  createdAt: string;
}

/**
 * Active MEL - an MEL item that has been issued for an aircraft
 */
export interface ActiveMel {
  id: number;
  aircraftId: number;
  melItemId: number;
  reportedById: number;
  reportedAt: string;
  description: string;
  expiresAt: string | null;
  status: string;
  clearedAt: string | null;
  clearedById: number | null;
  clearedNotes: string | null;
  aircraft: {
    id: number;
    tailNumber: string;
    aircraftType: string;
  };
  melItem: MelItem;
  reportedBy: {
    id: number;
    firstName: string;
    lastName: string;
  };
  clearedBy?: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

/**
 * Search match from smart search
 */
export interface MelSearchMatch {
  item: MelItem;
  score: number;
  matchType: 'exact' | 'partial' | 'semantic';
}

/**
 * AI explanation from smart search
 */
export interface MelExplanation {
  canDispatch: DispatchStatus;
  summary: string;
  selectedItem: {
    itemNumber: string;
    title: string;
    repairCategory: string;
  };
  conditions: string[];
  operationalNotes: string[];
  confidence: number;
  reasoning: string;
}

/**
 * Smart search response (matches backend SmartSearchResult)
 */
export interface SmartSearchResponse {
  items: MelSearchMatch[];
  aiExplanation: MelExplanation | null;
  searchMeta: {
    query: string;
    aircraftType: string;
    totalResults: number;
    keywordMatches: number;
    semanticMatches: number;
    hasEmbeddings: boolean;
    aiExplanationEnabled: boolean;
  };
}

/**
 * Smart search request body
 */
export interface SmartSearchRequest {
  query: string;
  aircraftType: string;
  ataChapter?: string;
  limit?: number;
  includeAIExplanation?: boolean;
  minSemanticSimilarity?: number;
}

/**
 * Issue MEL request body
 */
export interface IssueMelRequest {
  aircraftId: number;
  melItemId: number;
  description: string;
}

/**
 * Clear MEL request body
 */
export interface ClearMelRequest {
  notes: string;
}

/**
 * Dispatch status type
 */
export type DispatchStatus = 'YES' | 'NO' | 'CONDITIONAL';

/**
 * Repair category with time limits
 */
export const REPAIR_CATEGORIES: Record<string, { label: string; days: number | null }> = {
  'A': { label: 'Category A', days: null }, // As specified in remarks
  'B': { label: 'Category B - 3 Days', days: 3 },
  'C': { label: 'Category C - 10 Days', days: 10 },
  'D': { label: 'Category D - 120 Days', days: 120 },
};

/**
 * API response wrappers
 */
export interface MelDocumentListResponse {
  success: boolean;
  count: number;
  data: MelDocument[];
}

export interface MelDocumentResponse {
  success: boolean;
  data: MelDocument;
}

export interface ActiveMelListResponse {
  success: boolean;
  count: number;
  data: ActiveMel[];
}

export interface ActiveMelResponse {
  success: boolean;
  data: ActiveMel;
}

export interface SmartSearchApiResponse {
  success: boolean;
  data: SmartSearchResponse;
}
